import type { Record } from "@suse-o11y/record";

import type {
	FullStackPack,
	NodeId,
	StackPackConfiguration,
	StackPackStateChangeEvent,
	UnlockedStrategy,
} from "~/models/dto";

import type { TypedAction } from "../../../../../generic/redux/TypedRedux";
import type { ErrorData, StackPackInstanceData } from "../../../entities/stackPacks/StackPacksActions";

import {
	STACK_PACKS_ADD_CONFIGURATION,
	STACK_PACKS_DEPROVISION_ERROR,
	STACK_PACKS_DEPROVISION_START,
	STACK_PACKS_DEPROVISION_SUCCESS,
	STACK_PACKS_DETAILS_CLEAR_STATE,
	STACK_PACKS_DETAILS_INIT_FIELDS,
	STACK_PACKS_DETAILS_SET_FIELD,
	STACK_PACKS_PROVISION_ERROR,
	STACK_PACKS_PROVISION_START,
	STACK_PACKS_PROVISION_SUCCESS,
	STACK_PACKS_START_STATUS_UPDATE_STREAM,
	STACK_PACKS_STOP_STATUS_UPDATE_STREAM,
	STACK_PACKS_UPDATE_CONFIGURATION,
} from "./StackPacksDetailsActionTypes";

export type ClearStackPacksDetailsState = TypedAction<typeof STACK_PACKS_DETAILS_CLEAR_STATE>;

export const clearStackPacksDetailsState = (): ClearStackPacksDetailsState => ({
	type: STACK_PACKS_DETAILS_CLEAR_STATE,
});

export type SetStackPacksDetailsField = TypedAction<typeof STACK_PACKS_DETAILS_SET_FIELD> & {
	configId: NodeId | undefined;
	fieldName: string;
	fieldValue: string;
};

export const setStackPacksDetailsField = (
	configId: NodeId | undefined,
	fieldName: string,
	fieldValue: string,
): SetStackPacksDetailsField => ({
	type: STACK_PACKS_DETAILS_SET_FIELD,
	configId,
	fieldName,
	fieldValue,
});

interface StackPackData {
	stackPack: FullStackPack;
}

export interface StackPackDetailsInitializeData {
	stackPackConfigurations: Array<StackPackConfiguration>;
}

export type InitializeStackPacksDetails = TypedAction<typeof STACK_PACKS_DETAILS_INIT_FIELDS> &
	StackPackDetailsInitializeData;

export const initializeStackPacksDetails = (
	stackPackConfigurations: Array<StackPackConfiguration>,
): InitializeStackPacksDetails => ({
	type: STACK_PACKS_DETAILS_INIT_FIELDS,
	stackPackConfigurations,
});

export interface ProvisionStackPackData extends StackPackData {
	fields: Record<string, string>;
	configurationId?: NodeId;
	flag?: UnlockedStrategy;
}
export type ProvisionStackPack = TypedAction<typeof STACK_PACKS_PROVISION_START> & ProvisionStackPackData;
export const provisionStackPack = (
	stackPack: FullStackPack,
	fields: Record<string, string>,
	configurationId?: NodeId,
	flag?: UnlockedStrategy,
): ProvisionStackPack => ({
	type: STACK_PACKS_PROVISION_START,
	stackPack,
	fields,
	configurationId,
	flag,
});
export interface ProvisionStackPackSuccessData extends StackPackData {
	config: StackPackConfiguration;
}

export type ProvisionStackPackSuccess = TypedAction<typeof STACK_PACKS_PROVISION_SUCCESS> &
	ProvisionStackPackSuccessData;
export const provisionStackPackSuccess = (
	config: StackPackConfiguration,
	stackPack: FullStackPack,
): ProvisionStackPackSuccess => ({
	type: STACK_PACKS_PROVISION_SUCCESS,
	config,
	stackPack,
});

export type ProvisionStackPackError = TypedAction<typeof STACK_PACKS_PROVISION_ERROR> & StackPackData & ErrorData;
export const provisionStackPackError = (error: Error, stackPack: FullStackPack): ProvisionStackPackError => ({
	type: STACK_PACKS_PROVISION_ERROR,
	error,
	stackPack,
});

export interface DeprovisionStackPackData extends StackPackInstanceData, StackPackData {
	retry?: boolean;
}

export type DeprovisionStackPack = TypedAction<typeof STACK_PACKS_DEPROVISION_START> & DeprovisionStackPackData;
export const deprovisionStackPack = (
	stackPack: FullStackPack,
	configurationId: NodeId,
	retry?: boolean,
): DeprovisionStackPack => ({
	type: STACK_PACKS_DEPROVISION_START,
	stackPack,
	configurationId,
	retry,
});

export interface DeprovisionStackPacSuccesskData extends StackPackInstanceData {}

export type DeprovisionStackPackSuccess = TypedAction<typeof STACK_PACKS_DEPROVISION_SUCCESS> &
	DeprovisionStackPacSuccesskData;
export const deprovisionStackPackSuccess = (
	stackPack: FullStackPack,
	configurationId: NodeId,
): DeprovisionStackPackSuccess => ({
	type: STACK_PACKS_DEPROVISION_SUCCESS,
	stackPack,
	configurationId,
});

export interface DeprovisionErrorStackPackData extends StackPackInstanceData {}

export type DeprovisionStackPackError = TypedAction<typeof STACK_PACKS_DEPROVISION_ERROR> &
	DeprovisionErrorStackPackData &
	ErrorData;
export const deprovisionStackPackError = (
	stackPack: FullStackPack,
	configurationId: NodeId,
	error: Error,
): DeprovisionStackPackError => ({
	type: STACK_PACKS_DEPROVISION_ERROR,
	stackPack,
	configurationId,
	error,
});

export type StartStackPackStatusUpdateStream = TypedAction<typeof STACK_PACKS_START_STATUS_UPDATE_STREAM>;
export const startStackPackStatusUpdateStream = (): StartStackPackStatusUpdateStream => ({
	type: STACK_PACKS_START_STATUS_UPDATE_STREAM,
});

export interface UpdateConfigurationStatusData {
	message: StackPackStateChangeEvent;
}
export type UpdateConfigurationStatus = TypedAction<typeof STACK_PACKS_UPDATE_CONFIGURATION> &
	UpdateConfigurationStatusData;
export const updateConfigurationStatus = (message: StackPackStateChangeEvent): UpdateConfigurationStatus => ({
	type: STACK_PACKS_UPDATE_CONFIGURATION,
	message,
});

export type StopStackPackStatusUpdateStream = TypedAction<typeof STACK_PACKS_STOP_STATUS_UPDATE_STREAM>;
export const stopStackPackStatusUpdateStream = (): StopStackPackStatusUpdateStream => ({
	type: STACK_PACKS_STOP_STATUS_UPDATE_STREAM,
});

export type AddStackPackConfiguration = TypedAction<typeof STACK_PACKS_ADD_CONFIGURATION> & StackPackData;
export const addStackPackConfiguration = (stackPack: FullStackPack): AddStackPackConfiguration => ({
	type: STACK_PACKS_ADD_CONFIGURATION,
	stackPack,
});
